import { env, isLocal, isProduction, logLevel } from "@liberty-fund/config";
export type LogLevel = "debug" | "info" | "warn" | "error";
const logLevelMap: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

type LogDetails = Record<string, any>;

class ConsoleLogger {
  private logLevelIndex: number;

  constructor() {
    this.debug(
      ">>>>>>>>>>>>>>>>>>>>>> Logger using CONFIG <<<<<<<<<<<<<<<<<<<<<<",
    );
    this.debug("CONFIG > env", { env });
    this.debug("CONFIG > isLocal?", { isLocal });
    this.debug("CONFIG > isProduction?", { isProduction });
    this.logLevelIndex =
      logLevelMap[logLevel as LogLevel] ?? logLevelMap["info"];
  }

  // Error's message/name/stack are non-enumerable own properties, so a
  // bare `JSON.stringify(error)` — however deep it's nested in `details`,
  // e.g. `{ reason: caughtError }` — silently serializes to `{}` and the
  // real failure reason is lost. The replacer runs on every key/value
  // pair JSON.stringify visits, including nested ones, so this catches
  // an Error anywhere in the details tree.
  private jsonReplacer(_key: string, value: unknown) {
    if (value instanceof Error) {
      return { name: value.name, message: value.message, stack: value.stack };
    }
    return value;
  }

  private formatDetails(details: LogDetails) {
    const timestamp = new Date();
    try {
      return JSON.stringify({ timestamp, ...details }, this.jsonReplacer, 2);
    } catch (e) {
      // Never let logging crash the caller.
      return JSON.stringify(
        {
          timestamp,
          __loggerError: "Failed to serialize log details",
          error:
            e instanceof Error
              ? { name: e.name, message: e.message, stack: e.stack }
              : String(e),
        },
        null,
        2,
      );
    }
  }

  private shouldLog(logLevel: LogLevel) {
    const index = logLevelMap[logLevel] ?? logLevelMap["error"];
    const shouldLog = index >= this.logLevelIndex;
    return shouldLog;
  }

  private emitLog(logLevel: LogLevel, message: string, details?: LogDetails) {
    if (!this.shouldLog(logLevel)) {
      return;
    }

    const payload = details ? this.formatDetails(details) : undefined;
    switch (logLevel) {
      case "debug":
        console.debug(message, payload);
        break;
      case "info":
        console.info(message, payload);
        break;
      case "warn":
        console.warn(message, payload);
        break;
      case "error":
        console.error(message, payload);
        break;
    }
  }

  public info(message: string, details?: LogDetails): void {
    this.emitLog("info", message, details);
  }

  public warn(message: string, details?: LogDetails): void {
    this.emitLog("warn", message, details);
  }

  public error(message: string, details?: LogDetails): void {
    this.emitLog("error", message, details);
  }

  public debug(message: string, details?: LogDetails): void {
    this.emitLog("debug", message, details);
  }

  public cloudapps(subapp: string, code: string, details: LogDetails): void {
    console.info(
      `org.libertyfund.portal.${subapp} [${code}]`,
      JSON.stringify(details),
    );
  }
}

export const Logger = new ConsoleLogger();
export default Logger;
